import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * Subtitle sizes map to specific text sizes for different viewport presets:
 * - sm: text-base (compact)
 * - default: text-lg (tablet portrait)
 * - lg: text-xl (tablet landscape, iPad Pro)
 * - xl: text-2xl (large tablet, touchscreen PC)
 * - 2xl: text-3xl (4K displays)
 */
export type SubtitleSize = "sm" | "default" | "lg" | "xl" | "2xl";

const subtitleVariants = cva("text-muted-foreground", {
  variants: {
    size: {
      sm: "text-base",
      default: "text-lg",
      lg: "text-xl",
      xl: "text-2xl",
      "2xl": "text-3xl",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
    },
  },
  defaultVariants: {
    size: "lg",
    align: "center",
    weight: "normal",
  },
});

export interface SubtitleProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof subtitleVariants> {
  children: React.ReactNode;
}

/**
 * Subtitle component for secondary descriptive text below titles.
 * Use for explanatory text, instructions, and context.
 *
 * @example
 * ```tsx
 * <Title>Patient suchen</Title>
 * <Subtitle>Bitte geben Sie Ihren Nachnamen und Ihr Geburtsdatum ein</Subtitle>
 * ```
 */
const Subtitle = React.forwardRef<HTMLParagraphElement, SubtitleProps>(
  ({ className, size, align, weight, children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn(subtitleVariants({ size, align, weight }), className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);

Subtitle.displayName = "Subtitle";

export { Subtitle };
